// Tablet: čtení smlouvy a odeslání podpisu do telefonu klientky (/podpis/<t>, 3. 11. 2026, schválený mockup 1/2).
// Tablet se nikam nepřihlašuje. QR kód z mobilu trenéra platí pro první zařízení, které ho otevře
// (server mu vrátí klíč, drží se v sessionStorage). Server: /api/smlouva-podpis-verejne.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { Minus, Plus, ShieldCheck, Send, Smartphone, Clock, FileText, CircleCheck, Loader2, AlertCircle } from "lucide-react";
import { upravMd, kapitoly as kapitolyZMd } from "../lib/smlouvaMd.js";

const API = "/api/smlouva-podpis-verejne";
async function post(data) {
  const r = await fetch(API, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(data) });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || "Něco se nepovedlo, zkuste to prosím znovu.");
  return j;
}
const KLIC = (t) => `fw40_podpis_${t}`;
const nactiKlic = (t) => { try { return sessionStorage.getItem(KLIC(t)); } catch { return null; } };
const ulozKlic = (t, k) => { try { sessionStorage.setItem(KLIC(t), k); } catch { /* bez úložiště */ } };
const smazKlic = (t) => { try { sessionStorage.removeItem(KLIC(t)); } catch { /* bez úložiště */ } };
const PISMO = ["text-[17px]", "text-[19px]", "text-[22px]", "text-[25px]"];

function Pilulka({ a, b }) {
  return <span className="inline-flex items-center gap-1.5 rounded-full border border-white/25 bg-white/15 px-3 py-1 text-[13px] font-bold">{a}<span className="h-3 w-px bg-white/40" />{b}</span>;
}

function Obrazovka({ ikona: Ikona, nadpis, children, akce }) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-gradient-to-br from-brand-950 via-brand-900 to-brand-700 px-6 text-center text-white">
      <img src="/logo-bila.svg" alt="FitWay40" className="mb-10 h-10" />
      {Ikona && <span className="mb-6 flex h-24 w-24 items-center justify-center rounded-full border border-white/25 bg-white/15"><Ikona className="h-11 w-11" /></span>}
      <div className="font-serif text-3xl font-bold sm:text-4xl">{nadpis}</div>
      {children && <div className="mt-3 max-w-xl text-[19px] text-white/80">{children}</div>}
      {akce}
    </div>
  );
}

export default function PodpisTablet() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [chyba, setChyba] = useState("");
  const [busy, setBusy] = useState(false);
  const [zpetKeSmlouve, setZpetKeSmlouve] = useState(false);
  const [pismo, setPismo] = useState(1);
  const [aktivni, setAktivni] = useState(0);
  const [precteno, setPrecteno] = useState(0);
  const [testOdkaz, setTestOdkaz] = useState("");
  const cteniRef = useRef(null);
  const klic = useRef(nactiKlic(token));

  const nacti = useCallback(async () => {
    try {
      if (klic.current) setData(await post({ akce: "tablet_stav", t: token, k: klic.current }));
      else {
        const r = await post({ akce: "tablet_otevrit", t: token });
        klic.current = r.klic; ulozKlic(token, r.klic); setData(r);
      }
    } catch (e) { setChyba(e.message); }
  }, [token]);
  useEffect(() => { nacti(); }, [nacti]);

  // Čekání na podpis: stav každé 3 s.
  useEffect(() => {
    if (!data || data.stav !== "sms") return undefined;
    const id = setInterval(() => { post({ akce: "tablet_stav", t: token, k: klic.current }).then((r) => setData((d) => ({ ...d, ...r, smlouva: r.smlouva || d.smlouva }))).catch(() => {}); }, 3000);
    return () => clearInterval(id);
  }, [data?.stav, token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Po podpisu se tablet za chvíli vrátí na úvodní obrazovku (smlouva už na něm nezůstane).
  const [konec, setKonec] = useState(false);
  useEffect(() => {
    if (data?.stav !== "podepsano") return undefined;
    smazKlic(token);
    const id = setTimeout(() => setKonec(true), 15000);
    return () => clearTimeout(id);
  }, [data?.stav, token]);

  const md = useMemo(() => upravMd(data?.smlouva?.obsah_md || ""), [data?.smlouva?.obsah_md]);
  const kap = useMemo(() => kapitolyZMd(data?.smlouva?.obsah_md || ""), [data?.smlouva?.obsah_md]);

  // Kapitola, kterou klientka právě čte, a kam až dočetla (✓ v navigaci).
  const naScroll = () => {
    const m = cteniRef.current; if (!m) return;
    const h = [...m.querySelectorAll("h2")];
    let i = 0;
    h.forEach((x, j) => { if (x.offsetTop - m.offsetTop - 80 <= m.scrollTop) i = j; });
    if (m.scrollTop + m.clientHeight >= m.scrollHeight - 30) i = h.length - 1;
    setAktivni(i); setPrecteno((p) => Math.max(p, i));
  };
  const skok = (i) => {
    const m = cteniRef.current; if (!m) return;
    const h = m.querySelectorAll("h2")[i];
    if (h) m.scrollTo({ top: h.offsetTop - m.offsetTop - 10, behavior: "smooth" });
  };

  const poslat = async () => {
    setBusy(true); setChyba("");
    try {
      const r = await post({ akce: "poslat_sms", t: token, k: klic.current });
      setData((d) => ({ ...d, ...r, smlouva: r.smlouva || d.smlouva })); setZpetKeSmlouve(false);
      if (r.test_odkaz) setTestOdkaz(r.test_odkaz);
    } catch (e) { setChyba(e.message); } finally { setBusy(false); }
  };

  if (konec) return <Obrazovka ikona={CircleCheck} nadpis="Hotovo, děkujeme!">Tablet můžete vrátit trenérovi.</Obrazovka>;
  if (!data && chyba) return <Obrazovka ikona={AlertCircle} nadpis="Odkaz neplatí">{chyba}</Obrazovka>;
  if (!data) return <Obrazovka nadpis={<span className="inline-flex items-center gap-3"><Loader2 className="h-8 w-8 animate-spin" />Otevírám smlouvu…</span>} />;
  if (data.stav === "podepsano") return <Obrazovka ikona={CircleCheck} nadpis="Smlouva je podepsaná">Děkujeme! Kopii smlouvy vám posíláme e‑mailem.</Obrazovka>;
  if (data.stav === "sms" && !zpetKeSmlouve) return (
    <Obrazovka ikona={Smartphone} nadpis="Podívejte se do telefonu"
      akce={<>
        <div className="mt-8 flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-2 text-[15px]"><Clock className="h-4 w-4" />Čekáme na podpis…</div>
        {testOdkaz && <a href={testOdkaz} target="_blank" rel="noreferrer" className="mt-4 rounded-xl bg-amber-300 px-4 py-2 text-[15px] font-bold text-brand-950">Test: otevřít odkaz pro telefon</a>}
        {chyba && <div className="mt-4 text-[15px] text-rose-200">{chyba}</div>}
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <button type="button" className="inline-flex min-h-[48px] items-center gap-2 rounded-xl border border-white/30 bg-white/10 px-5 text-[16px] font-bold" onClick={() => setZpetKeSmlouve(true)}><FileText className="h-5 w-5" />Zpět ke smlouvě</button>
          <button type="button" className="inline-flex min-h-[48px] items-center gap-2 rounded-xl border border-white/30 bg-white/10 px-5 text-[16px] font-bold" onClick={poslat} disabled={busy}><Send className="h-5 w-5" />SMS nepřišla, poslat znovu</button>
        </div>
      </>}>
      Poslali jsme vám SMS s odkazem na podpis na číslo <strong className="text-white">{data.smlouva?.telefon}</strong>.
    </Obrazovka>
  );

  const s = data.smlouva || {};
  return (
    <div className="flex h-[100dvh] flex-col bg-slate-50">
      <header className="bg-gradient-to-r from-brand-950 via-brand-900 to-brand-700 px-4 py-3 text-white sm:px-8 sm:py-4">
        <div className="flex items-center gap-4 sm:gap-5">
          <img src="/logo-bila.svg" alt="FitWay40" className="hidden h-8 sm:block" />
          <span className="hidden h-8 w-px bg-white/25 sm:block" />
          <div className="min-w-0 flex-1">
            <div className="font-serif text-xl font-bold leading-tight sm:text-2xl">{s.nazev}</div>
            <div className="mt-1.5 flex flex-wrap gap-2">
              <Pilulka a="Klientka" b={s.jmeno} />
              {s.trener && <Pilulka a="Trenér" b={s.trener} />}
              <Pilulka a="Datum" b={s.datum} />
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1 rounded-xl border border-white/25 bg-white/10 p-1">
            <button type="button" aria-label="Menší písmo" className="flex h-11 w-11 items-center justify-center rounded-lg text-white disabled:opacity-40" disabled={pismo === 0} onClick={() => setPismo((x) => Math.max(0, x - 1))}><Minus className="h-5 w-5" /></button>
            <span className="hidden px-1 text-[15px] font-bold sm:inline">Písmo</span>
            <button type="button" aria-label="Větší písmo" className="flex h-11 w-11 items-center justify-center rounded-lg bg-white/20 text-white disabled:opacity-40" disabled={pismo === PISMO.length - 1} onClick={() => setPismo((x) => Math.min(PISMO.length - 1, x + 1))}><Plus className="h-5 w-5" /></button>
          </div>
        </div>
      </header>
      <div className="flex min-h-0 flex-1">
        {kap.length > 0 && (
          <nav className="hidden w-64 shrink-0 overflow-y-auto border-r border-slate-200 bg-white px-4 py-5 md:block">
            <div className="mb-3 px-2 text-[12px] font-bold uppercase tracking-wide text-slate-500">Kapitoly</div>
            {kap.map((k, i) => (
              <button key={i} type="button" onClick={() => skok(i)} className={`mb-1 flex min-h-[44px] w-full items-center gap-2.5 rounded-xl px-3 text-left text-[15px] ${i === aktivni ? "bg-brand-50 font-bold text-brand-deep" : "text-slate-700"}`}>
                <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[12px] font-bold ${i < aktivni || (i <= precteno && i !== aktivni) ? "bg-emerald-100 text-emerald-700" : i === aktivni ? "bg-brand text-white" : "bg-slate-100 text-slate-500"}`}>{i < aktivni || (i <= precteno && i !== aktivni) ? "✓" : k.cislo || i + 1}</span>
                <span className="min-w-0">{k.nazev}</span>
              </button>
            ))}
          </nav>
        )}
        <main ref={cteniRef} onScroll={naScroll} className="min-h-0 flex-1 overflow-y-auto px-5 py-6 sm:px-10">
          <article className={`prose prose-slate mx-auto max-w-3xl leading-relaxed prose-h1:font-serif prose-h1:text-3xl prose-h2:mt-8 prose-h2:font-serif prose-h2:text-2xl prose-h2:text-brand-950 prose-strong:text-slate-900 ${PISMO[pismo]}`}>
            <ReactMarkdown>{md}</ReactMarkdown>
          </article>
        </main>
      </div>
      <footer className="flex flex-wrap items-center gap-x-5 gap-y-3 border-t border-slate-200 bg-white px-4 py-3 shadow-[0_-6px_20px_rgba(36,20,48,0.06)] sm:px-8 sm:py-4">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-50 text-brand"><Smartphone className="h-5 w-5" /></span>
          <div>
            <div className="text-[13px] text-slate-500">Podpis přijde SMS na telefon</div>
            <div className="text-[17px] font-bold text-slate-900">{s.telefon}</div>
          </div>
        </div>
        <div className="hidden items-center gap-2 text-[14px] text-slate-500 lg:flex"><ShieldCheck className="h-5 w-5 text-emerald-600" />Odkaz platí jen pro tuto smlouvu</div>
        <span className="flex-1" />
        {chyba && <div className="w-full text-[14px] font-bold text-rose-700 sm:w-auto">{chyba}</div>}
        <button type="button" className="btn-primary min-h-[56px] w-full justify-center px-6 text-[17px] sm:w-auto" onClick={data.stav === "sms" ? () => setZpetKeSmlouve(false) : poslat} disabled={busy}>
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}{data.stav === "sms" ? "Zpět na čekání na podpis" : "Poslat k podpisu do telefonu"}
        </button>
      </footer>
    </div>
  );
}
